import React, { useEffect } from "react";

// Hooks
import { createContext, useContext, useState } from "react";

// Services
import ReactGA from "react-ga4";
import { ApiService } from "../services";

// Assets
import { debug } from "../App";
import { defaultSettings } from "../defaultSettings";
import initializeI18next from "../i18n";
import { hexToRgb } from "../utils/hexToRgb";
import useTraceUpdate from "../utils/useTraceUpdate";

const SettingsContext = createContext<any>(null);

export function useSettings() {
  return useContext(SettingsContext);
}

export function SettingsProvider({ children }: { children: any }) {
  const [loading, setLoading] = useState<boolean>(true);
  const [settings, setSettings] = useState<any>(defaultSettings);
  useTraceUpdate(children);

  initializeI18next(settings);

  const refreshSettings = (newSettings?: any) => {
    if (newSettings) {
      setSettings(newSettings);
      setLoading(false);
    } else {
      setLoading(true);
      ApiService.get("settings")
        .then((response: any) => {
          setSettings(response);

          // Initialise Google Analytics
          const googleAnalyticsMeasurementId = response?.miscellaneous?.google_analytics_measurement_id;
          if (googleAnalyticsMeasurementId) {
            // @ts-ignore
            ReactGA.initialize(googleAnalyticsMeasurementId);
          }

          debug("SettingsContext loaded");
        })
        .catch((error: any) => {
          debug("SettingsContext error:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  useEffect(() => {
    if (settings?.brand) {
      mapColoursToCSSVariables(settings);
    }

    // Re-initialise i18n
    initializeI18next(settings);
  }, [settings]);

  return <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>{loading ? <div className="settings-loader" /> : children}</SettingsContext.Provider>;
}

function mapColoursToCSSVariables(settings: any) {
  const root = document.documentElement;
  Object.entries(settings?.brand).forEach(([key, value]) => {
    if (typeof value === "string" && value.startsWith("#")) {
      root.style.setProperty(`--${key}`, value);

      // Set the rgb version of this colour
      const rgb = hexToRgb(value);
      if (rgb) {
        root.style.setProperty(`--${key}-rgb`, [rgb.r, rgb.g, rgb.b].join(", "));
        debug(`Setting CSS variable --${key} to ${value} and --${key}-rgb to ${[rgb.r, rgb.g, rgb.b].join(", ")}`);
      }
    }
  });
}
